import React, { useState, useEffect } from "react";
import "./PrakritiAssessmentTab.css";
import { prakritiQuestionsData } from "../../../utils/prakritiQuestionsData";
import {
  getPatientPrakritiHistory,
  createPrakritiAssessment,
  deletePrakritiAssessment
} from "../../../api/prakriti";
import { getPatientById } from "../../../api/patient";
import PrakritiReportPrintModal from "./PrakritiReportPrintModal";
import {
  FaPlus,
  FaArrowLeft,
  FaCalendarAlt,
  FaFileAlt,
  FaTrashAlt,
  FaChevronRight,
  FaChevronLeft,
  FaSave,
  FaLeaf,
  FaUser,
  FaUtensils,
  FaClipboardList,
  FaChartPie,
} from "react-icons/fa";

const STEP_META = [
  { id: 1, label: "Personal & Measurements", labelMr: "वैयक्तिक व शारीरिक", icon: FaUser },
  { id: 2, label: "Diet & Health", labelMr: "आहार व आरोग्य", icon: FaUtensils },
  { id: 3, label: "Questionnaire", labelMr: "प्रश्नावली", icon: FaClipboardList },
  { id: 4, label: "Result & Diagnosis", labelMr: "मोजणी व निष्कर्ष", icon: FaChartPie },
];

const PrakritiAssessmentTab = ({ patientId }) => {
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [formData, setFormData] = useState({
    maritalStatus: "",
    education: "",
    occupation: "",
    physicalMeasurements: {
      heightCm: "",
      weightKg: "",
      exerciseType: "",
      sleepHoursPerNight: ""
    },
    dietaryHabits: {
      dietType: "शाकाहारी",
      mealsPerDay: "",
      mealTimings: "",
      beverageIntake: "",
      waterIntakeLiters: "",
      fruitConsumption: "",
      vegetableConsumption: "",
      sweetsConsumption: "",
      friedOrProcessedFood: "",
      fastingDetails: ""
    },
    healthInfo: {
      knownMedicalIllnesses: "",
      digestiveIssues: "",
      pastSurgeries: "",
      foodAllergies: "",
      currentMedications: "",
      habitsAndAddictions: ""
    },
    answers: {},
    dominantPrakriti: "",
    diagnosisConclusion: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [patientId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (patientId) {
        const [patRes, histRes] = await Promise.all([
          getPatientById(patientId).catch(() => null),
          getPatientPrakritiHistory(patientId).catch(() => null)
        ]);

        if (patRes?.data) {
          // get-patient returns { result: patient }; fall back to other shapes if needed
          const patData =
            patRes.data.result ||
            patRes.data.patient ||
            patRes.data.data ||
            patRes.data;
          setPatient(patData);
          setFormData((prev) => ({
            ...prev,
            maritalStatus: patData.maritalStatus || "",
            education: patData.education || "",
            occupation: patData.occupation || ""
          }));
        }

        if (histRes?.data?.data) {
          setHistory(histRes.data.data);
        }
      }
    } catch (err) {
      console.error("Error fetching Prakriti data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleNestedChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleAnswerSelect = (questionId, optionKey) => {
    setFormData((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: optionKey
      }
    }));
  };

  const deriveDominantPrakriti = (vata, pitta, kapha) => {
    const maxScore = Math.max(vata, pitta, kapha);
    if (maxScore === 0) return "";

    const dominantTypesMr = [];
    const dominantTypesEn = [];

    if (vata === maxScore) {
      dominantTypesMr.push("वात");
      dominantTypesEn.push("Vata");
    }
    if (pitta === maxScore) {
      dominantTypesMr.push("पित्त");
      dominantTypesEn.push("Pitta");
    }
    if (kapha === maxScore) {
      dominantTypesMr.push("कफ");
      dominantTypesEn.push("Kapha");
    }

    if (dominantTypesMr.length === 3) {
      return "त्रिदोषज प्रकृती (Tridoshaj Prakriti)";
    }

    const mrStr = dominantTypesMr.join("-");
    const enStr = dominantTypesEn.join("-");
    return `${mrStr} प्रकृती (${enStr} Prakriti)`;
  };

  // Auto-prefill Dominant Prakriti live whenever answers change
  useEffect(() => {
    let vata = 0, pitta = 0, kapha = 0;
    Object.values(formData.answers).forEach((val) => {
      if (val === "A") vata++;
      if (val === "B") pitta++;
      if (val === "C") kapha++;
    });

    if (vata > 0 || pitta > 0 || kapha > 0) {
      const calculatedPrakriti = deriveDominantPrakriti(vata, pitta, kapha);
      if (calculatedPrakriti) {
        setFormData((prev) => ({
          ...prev,
          dominantPrakriti: calculatedPrakriti
        }));
      }
    }
  }, [formData.answers]);

  const calculateLiveScores = () => {
    let vata = 0, pitta = 0, kapha = 0;
    Object.values(formData.answers).forEach((val) => {
      if (val === "A") vata++;
      if (val === "B") pitta++;
      if (val === "C") kapha++;
    });
    return { vata, pitta, kapha };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientId) {
      alert("Patient ID is missing!");
      return;
    }

    try {
      setSubmitting(true);
      const formattedAnswers = Object.entries(formData.answers).map(([qNum, opt]) => ({
        questionNumber: Number(qNum),
        selectedOption: opt
      }));

      const payload = {
        patientId,
        maritalStatus: formData.maritalStatus,
        education: formData.education,
        occupation: formData.occupation,
        physicalMeasurements: formData.physicalMeasurements,
        dietaryHabits: formData.dietaryHabits,
        healthInfo: formData.healthInfo,
        answers: formattedAnswers,
        dominantPrakriti: formData.dominantPrakriti,
        diagnosisConclusion: formData.diagnosisConclusion,
        notes: formData.notes
      };

      const res = await createPrakritiAssessment(payload);
      if (res.data?.success) {
        alert("Prakriti Assessment saved successfully!");
        setShowForm(false);
        fetchData();
      }
    } catch (err) {
      console.error("Error submitting Prakriti assessment:", err);
      alert(err.response?.data?.message || "Failed to save assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Prakriti Assessment?")) {
      try {
        await deletePrakritiAssessment(id);
        fetchData();
      } catch (err) {
        alert("Failed to delete assessment");
      }
    }
  };

  const liveScores = calculateLiveScores();
  const answeredCount = Object.keys(formData.answers).length;

  if (loading) {
    return (
      <div className="prakriti-loading">
        <div className="prakriti-spinner" />
        <span>Loading Prakriti Assessment records...</span>
      </div>
    );
  }

  return (
    <div className="prakriti-tab-wrapper">
      <div className="prakriti-tab-header">
        <div className="prakriti-header-text">
          <div className="prakriti-title-row">
            <span className="prakriti-icon-badge" aria-hidden="true">
              <FaLeaf />
            </span>
            <div>
              <h2>आयुर्वेदिक प्रकृती परीक्षण</h2>
              <p>Ayurvedic Prakriti Assessment — questionnaire & health profile</p>
            </div>
          </div>
        </div>
        {!showForm ? (
          <button type="button" className="btn-new-assessment" onClick={() => setShowForm(true)}>
            <FaPlus /> New Assessment
          </button>
        ) : (
          <button type="button" className="btn-cancel-assessment" onClick={() => setShowForm(false)}>
            <FaArrowLeft /> Back to History
          </button>
        )}
      </div>

      {!showForm && (
        <div className="prakriti-history-section">
          {history.length === 0 ? (
            <div className="empty-prakriti-card">
              <div className="empty-prakriti-icon">
                <FaClipboardList />
              </div>
              <h4>No assessments yet</h4>
              <p>No Prakriti assessments recorded for this patient.</p>
              <button type="button" className="btn-new-assessment" onClick={() => setShowForm(true)}>
                <FaPlus /> Start First Assessment
              </button>
            </div>
          ) : (
            <div className="history-cards-grid">
              {history.map((item) => (
                <div key={item._id} className="history-card">
                  <div className="history-card-header">
                    <span className="assessment-date">
                      <FaCalendarAlt />
                      {new Date(item.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric"
                      })}
                    </span>
                    <span className="prakriti-tag">
                      {item.dominantPrakriti || "Assessed"}
                    </span>
                  </div>
                  <div className="history-scores">
                    <div className="score-pill vata">
                      <span className="pill-dot" />
                      वात {item.scores?.vataCount || 0}
                    </div>
                    <div className="score-pill pitta">
                      <span className="pill-dot" />
                      पित्त {item.scores?.pittaCount || 0}
                    </div>
                    <div className="score-pill kapha">
                      <span className="pill-dot" />
                      कफ {item.scores?.kaphaCount || 0}
                    </div>
                  </div>
                  {item.diagnosisConclusion && (
                    <p className="history-notes">
                      <strong>निदान:</strong> {item.diagnosisConclusion}
                    </p>
                  )}
                  <div className="history-card-actions">
                    <button
                      type="button"
                      className="btn-view"
                      onClick={() => setSelectedReport(item)}
                    >
                      <FaFileAlt /> View & Print
                    </button>
                    <button
                      type="button"
                      className="btn-delete"
                      onClick={() => handleDelete(item._id)}
                      title="Delete assessment"
                      aria-label="Delete assessment"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="prakriti-form">
          <div className="stepper-navigation" role="tablist">
            {STEP_META.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              const isDone = activeStep > step.id;
              return (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`step-btn ${isActive ? "active" : ""} ${isDone ? "done" : ""}`}
                  onClick={() => setActiveStep(step.id)}
                >
                  <span className="step-num">
                    {isDone ? "✓" : step.id}
                  </span>
                  <span className="step-copy">
                    <span className="step-label-mr">{step.labelMr}</span>
                    <span className="step-label-en">
                      {step.id === 3 ? `${step.label} (${answeredCount}/${prakritiQuestionsData.length})` : step.label}
                    </span>
                  </span>
                  <Icon className="step-icon" aria-hidden="true" />
                </button>
              );
            })}
          </div>

          {activeStep === 1 && (
            <div className="form-step-content">
              <div className="section-heading">
                <h3>१. वैयक्तिक तपशील</h3>
                <span>Personal Details</span>
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label htmlFor="maritalStatus">वैवाहिक स्थिती (Marital Status)</label>
                  <div className="select-wrap">
                    <select
                      id="maritalStatus"
                      value={formData.maritalStatus}
                      onChange={(e) => setFormData({ ...formData, maritalStatus: e.target.value })}
                    >
                      <option value="">Select Status</option>
                      <option value="विवाहित (Married)">विवाहित (Married)</option>
                      <option value="अविवाहित (Unmarried)">अविवाहित (Unmarried)</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="education">शिक्षण (Education)</label>
                  <input
                    id="education"
                    type="text"
                    value={formData.education}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    placeholder="e.g. Graduate, HSC"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="occupation">व्यवसाय (Occupation)</label>
                  <input
                    id="occupation"
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                    placeholder="e.g. Service, Business"
                  />
                </div>
              </div>

              <div className="section-heading">
                <h3>२. शारीरिक मापे आणि जीवनशैली</h3>
                <span>Measurements & Lifestyle</span>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label htmlFor="heightCm">उंची (सेमी) — Height in cm</label>
                  <input
                    id="heightCm"
                    type="number"
                    value={formData.physicalMeasurements.heightCm}
                    onChange={(e) => handleNestedChange("physicalMeasurements", "heightCm", e.target.value)}
                    placeholder="170"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="weightKg">वजन (किलो) — Weight in kg</label>
                  <input
                    id="weightKg"
                    type="number"
                    value={formData.physicalMeasurements.weightKg}
                    onChange={(e) => handleNestedChange("physicalMeasurements", "weightKg", e.target.value)}
                    placeholder="65"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="exerciseType">व्यायामाचा प्रकार (Exercise Type)</label>
                  <input
                    id="exerciseType"
                    type="text"
                    value={formData.physicalMeasurements.exerciseType}
                    onChange={(e) => handleNestedChange("physicalMeasurements", "exerciseType", e.target.value)}
                    placeholder="e.g. Yoga, Walking, Running"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="sleepHours">झोप (तास/रात्र) — Sleep Hours</label>
                  <input
                    id="sleepHours"
                    type="text"
                    value={formData.physicalMeasurements.sleepHoursPerNight}
                    onChange={(e) => handleNestedChange("physicalMeasurements", "sleepHoursPerNight", e.target.value)}
                    placeholder="e.g. 7-8 hours"
                  />
                </div>
              </div>

              <div className="step-actions">
                <span />
                <button type="button" className="btn-next" onClick={() => setActiveStep(2)}>
                  Next: Diet & Health <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {activeStep === 2 && (
            <div className="form-step-content">
              <div className="section-heading">
                <h3>३. आहारविषयक सवयी</h3>
                <span>Dietary Habits</span>
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label htmlFor="dietType">आहार प्रकार (Diet Type)</label>
                  <div className="select-wrap">
                    <select
                      id="dietType"
                      value={formData.dietaryHabits.dietType}
                      onChange={(e) => handleNestedChange("dietaryHabits", "dietType", e.target.value)}
                    >
                      <option value="शाकाहारी">शाकाहारी (Vegetarian)</option>
                      <option value="मांसाहारी">मांसाहारी (Non-Vegetarian)</option>
                      <option value="व्हिगन">व्हिगन (Vegan)</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="mealsPerDay">जेवणाची संख्या (Meals/day)</label>
                  <input
                    id="mealsPerDay"
                    type="text"
                    value={formData.dietaryHabits.mealsPerDay}
                    onChange={(e) => handleNestedChange("dietaryHabits", "mealsPerDay", e.target.value)}
                    placeholder="e.g. 2 times, 3 times"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="waterIntake">पाणी पिणे (लिटर/दिवस)</label>
                  <input
                    id="waterIntake"
                    type="text"
                    value={formData.dietaryHabits.waterIntakeLiters}
                    onChange={(e) => handleNestedChange("dietaryHabits", "waterIntakeLiters", e.target.value)}
                    placeholder="e.g. 3-4 Liters"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="mealTimings">जेवणाची वेळ (Meal Timings)</label>
                  <input
                    id="mealTimings"
                    type="text"
                    value={formData.dietaryHabits.mealTimings}
                    onChange={(e) => handleNestedChange("dietaryHabits", "mealTimings", e.target.value)}
                    placeholder="e.g. Regular 1pm & 8pm"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="beverageIntake">पेयांचे सेवन (Tea/Coffee/Juices)</label>
                  <input
                    id="beverageIntake"
                    type="text"
                    value={formData.dietaryHabits.beverageIntake}
                    onChange={(e) => handleNestedChange("dietaryHabits", "beverageIntake", e.target.value)}
                    placeholder="e.g. Tea twice daily"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="sweets">गोड पदार्थांचे सेवन (Sweets)</label>
                  <input
                    id="sweets"
                    type="text"
                    value={formData.dietaryHabits.sweetsConsumption}
                    onChange={(e) => handleNestedChange("dietaryHabits", "sweetsConsumption", e.target.value)}
                    placeholder="e.g. Moderate"
                  />
                </div>
              </div>

              <div className="section-heading">
                <h3>४. आरोग्य माहिती</h3>
                <span>Health History</span>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label htmlFor="medicalHistory">ज्ञात वैद्यकीय आजार (Medical History)</label>
                  <input
                    id="medicalHistory"
                    type="text"
                    value={formData.healthInfo.knownMedicalIllnesses}
                    onChange={(e) => handleNestedChange("healthInfo", "knownMedicalIllnesses", e.target.value)}
                    placeholder="e.g. Hypertension, Diabetes, None"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="digestiveIssues">पचन / पोटाचे त्रास (Digestive Issues)</label>
                  <input
                    id="digestiveIssues"
                    type="text"
                    value={formData.healthInfo.digestiveIssues}
                    onChange={(e) => handleNestedChange("healthInfo", "digestiveIssues", e.target.value)}
                    placeholder="e.g. Acidity, Constipation"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pastSurgeries">झालेली शस्त्रक्रिया (Past Surgeries)</label>
                  <input
                    id="pastSurgeries"
                    type="text"
                    value={formData.healthInfo.pastSurgeries}
                    onChange={(e) => handleNestedChange("healthInfo", "pastSurgeries", e.target.value)}
                    placeholder="e.g. Appendectomy, None"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="foodAllergies">अन्नाची ऍलर्जी (Food Allergies)</label>
                  <input
                    id="foodAllergies"
                    type="text"
                    value={formData.healthInfo.foodAllergies}
                    onChange={(e) => handleNestedChange("healthInfo", "foodAllergies", e.target.value)}
                    placeholder="e.g. Gluten, Milk, None"
                  />
                </div>
              </div>

              <div className="step-actions">
                <button type="button" className="btn-prev" onClick={() => setActiveStep(1)}>
                  <FaChevronLeft /> Back
                </button>
                <button type="button" className="btn-next" onClick={() => setActiveStep(3)}>
                  Next: 24 Questions <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {activeStep === 3 && (
            <div className="form-step-content">
              <div className="questions-header">
                <div className="section-heading" style={{ marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>
                  <h3>५. प्रकृती परीक्षण प्रश्नावली</h3>
                  <span>{prakritiQuestionsData.length} Questions</span>
                </div>
                <div className="live-score-bar">
                  <span className="pill vata">
                    <span className="pill-dot" />
                    वात {liveScores.vata}
                  </span>
                  <span className="pill pitta">
                    <span className="pill-dot" />
                    पित्त {liveScores.pitta}
                  </span>
                  <span className="pill kapha">
                    <span className="pill-dot" />
                    कफ {liveScores.kapha}
                  </span>
                  <span className="answered-chip">{answeredCount}/{prakritiQuestionsData.length} answered</span>
                </div>
              </div>

              <div className="questions-list">
                {prakritiQuestionsData.map((q) => {
                  const currentAns = formData.answers[q.id];
                  return (
                    <div key={q.id} className={`question-card ${currentAns ? "answered" : ""}`}>
                      <div className="question-title">
                        <span className="q-number">{String(q.id).padStart(2, "0")}</span>
                        <div className="q-text">
                          <strong>{q.titleMr}</strong>
                          <span className="subtitle">{q.titleEn}</span>
                        </div>
                      </div>
                      <div className="options-grid" role="radiogroup" aria-label={`Question ${q.id}`}>
                        {["A", "B", "C"].map((optKey) => {
                          const option = q.options[optKey];
                          const isSelected = currentAns === optKey;
                          const doshaClass =
                            optKey === "A" ? "opt-vata" : optKey === "B" ? "opt-pitta" : "opt-kapha";
                          return (
                            <label
                              key={optKey}
                              className={`option-card ${doshaClass} ${isSelected ? "selected" : ""}`}
                            >
                              <input
                                type="radio"
                                name={`q_${q.id}`}
                                value={optKey}
                                checked={isSelected}
                                onChange={() => handleAnswerSelect(q.id, optKey)}
                              />
                              <span className="option-radio" aria-hidden="true" />
                              <div className="option-text">
                                <strong>
                                  <span className="opt-key">{optKey}</span> {option.mr}
                                </strong>
                                <small>{option.en}</small>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="step-actions">
                <button type="button" className="btn-prev" onClick={() => setActiveStep(2)}>
                  <FaChevronLeft /> Back
                </button>
                <button type="button" className="btn-next" onClick={() => setActiveStep(4)}>
                  Next: Results <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {activeStep === 4 && (
            <div className="form-step-content">
              <div className="section-heading">
                <h3>६. प्रकृती मोजणी व निष्कर्ष</h3>
                <span>Summary & Diagnosis</span>
              </div>

              <div className="score-summary-card">
                <h4>गणना / Scoring Summary</h4>
                <div className="scores-display">
                  <div className="score-circle vata">
                    <span className="score-num">{liveScores.vata}</span>
                    <span className="score-label">वात (A)</span>
                  </div>
                  <div className="score-circle pitta">
                    <span className="score-num">{liveScores.pitta}</span>
                    <span className="score-label">पित्त (B)</span>
                  </div>
                  <div className="score-circle kapha">
                    <span className="score-num">{liveScores.kapha}</span>
                    <span className="score-label">कफ (C)</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="dominantPrakriti">
                  प्रकृती निष्कर्ष (Dominant Prakriti)
                  {answeredCount > 0 && (
                    <small style={{ color: "#2e7d32", marginLeft: "10px", fontWeight: "600" }}>
                      (Auto-filled based on highest score)
                    </small>
                  )}
                </label>
                <input
                  id="dominantPrakriti"
                  type="text"
                  value={formData.dominantPrakriti}
                  onChange={(e) => setFormData({ ...formData, dominantPrakriti: e.target.value })}
                  placeholder="e.g. वात-पित्त प्रकृती (Vata-Pitta Prakriti)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="diagnosisConclusion">वैद्यकीय टिप्पणी / निदान (Doctor&apos;s Notes)</label>
                <textarea
                  id="diagnosisConclusion"
                  rows="4"
                  value={formData.diagnosisConclusion}
                  onChange={(e) => setFormData({ ...formData, diagnosisConclusion: e.target.value })}
                  placeholder="Enter diagnosis notes or dietary advice..."
                />
              </div>

              <div className="step-actions">
                <button type="button" className="btn-prev" onClick={() => setActiveStep(3)}>
                  <FaChevronLeft /> Back
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  <FaSave />
                  {submitting ? "Saving..." : "Save Assessment"}
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      {selectedReport && (
        <PrakritiReportPrintModal
          assessment={selectedReport}
          patient={patient}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
};

export default PrakritiAssessmentTab;
